import { useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { useStore } from 'common/store';
import { ParserParams } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { SHEET_CONFIG_CONSTANTS } from 'pages/Funds/common/constants/fundSummary';
import { useCellCustomClasses } from 'pages/Funds/hooks';
import { getArrayValue, getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { dbShortDate } from 'utilities/dateFormats';
import { ISO_DATE_FORMAT } from 'utilities/dateFormats/constants';
import { createColumns, createParser, createRows, FundSummaryColumn } from './config';
import { UseFundSummarySheet } from './types';

const { FUND_SUMMARY_SPREADSHEET_TABLE_TERMS } = SHEET_CONFIG_CONSTANTS;

const useFundSummarySheet: UseFundSummarySheet = params => {
  const { fundSummary, measurementDate, tasksProgress } = params;

  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo, fundInfo } = storeValue;
  const {
    are_fund_investments_enabled: areFundInvestmentsEnabled = false,
    show_process_management_column: isProcessManagementEnabled = false,
  } = getObjectValue(firmInfo);

  const { cellCustomClasses } = useCellCustomClasses();

  const companies = useMemo(() => getArrayValue(fundSummary?.companies), [fundSummary]);

  // Use backend-calculated Gross IRR (includes all cash flows from FundOwnershipDetails)
  const fundGrossIRR = useMemo(
    () => getNumberValue(fundSummary?.fund_gross_irr),
    [fundSummary]
  );

  const fundInvestments = useMemo(() => getArrayValue(fundSummary?.fund_investments), [fundSummary]);
  const portfolioCompaniesFundGrossIRR = useMemo(
    () => getNumberValue(fundSummary?.portfolio_companies_fund_gross_irr),
    [fundSummary]
  );

  const companyCount = useMemo(() => companies.length, [companies]);
  const fundInvestmentCount = useMemo(() => fundInvestments.length, [fundInvestments]);

  // Create rows for Fund Summary Spreadsheet
  const rows = useMemo(
    () =>
      createRows({
        areFundInvestmentsEnabled,
        companies,
        companyCount,
        firmInfo,
        fundInvestmentCount,
        fundInvestments,
        measurementDate,
      }),
    [
      areFundInvestmentsEnabled,
      companies,
      companyCount,
      firmInfo,
      fundInvestmentCount,
      fundInvestments,
      measurementDate,
    ]
  );

  // Create columns for Fund Summary Spreadsheet
  const columns = useMemo(() => {
    if (!isEmpty(companies) || !isEmpty(fundInvestments)) {
      return createColumns({ companies, fundInvestments, isProcessManagementEnabled });
    }

    return [];
  }, [companies, fundInvestments, isProcessManagementEnabled]);

  // Create parser for Fund Summary Spreadsheet
  const parser = useCallback(
    (props: ParserParams<FundSummaryColumn>) =>
      createParser({
        ...props,
        areFundInvestmentsEnabled,
        cellCustomClasses,
        companyCount,
        fundGrossIRR,
        portfolioCompaniesFundGrossIRR,
        fundInvestmentCount,
        fundName: getStringValue(fundInfo?.name),
        isProcessManagementEnabled,
        measurementDateName: dbShortDate(measurementDate?.name, ISO_DATE_FORMAT) ?? '',
        tasksProgress,
      }),
    [
      areFundInvestmentsEnabled,
      cellCustomClasses,
      companyCount,
      fundGrossIRR,
      fundInfo,
      fundInvestmentCount,
      isProcessManagementEnabled,
      measurementDate,
      portfolioCompaniesFundGrossIRR,
      tasksProgress,
    ]
  );

  // Create Spreadsheet config
  const spreadsheet = useMemo(() => {
    if (!isEmpty(columns)) {
      // @ts-expect-error: SpreadsheetConfigConstructorParams requires runtime methods not needed by this config.
      const spreadsheetConfig = new SpreadsheetConfig({
        allowConfirmAndDeleteColumn: false,
        alwaysDisplayLegend: false,
        colTitleRow: 0, // Add header row styles
        columns,
        currencyFormatter: true,
        format: undefined,
        hasColTitle: true, // Add styles to header row (colTitleRow)
        name: FUND_SUMMARY_SPREADSHEET_TABLE_TERMS.tableName,
        page: FUND_SUMMARY_SPREADSHEET_TABLE_TERMS.tableName as SpreadsheetConfig['page'],
        parser: parser as unknown as SpreadsheetConfig['parser'],
        rowConfig: rows,
        showPreviousColsDivider: false,
        showTitlesColumn: true,
        reverseParser: undefined,
        showToolbar: true,
        showTotalColumn: false,
        tableData: { columns },
        tableTerms: FUND_SUMMARY_SPREADSHEET_TABLE_TERMS as SpreadsheetConfig['tableTerms'],
        totalParser: undefined,
        unitsFormatter: true,
      });

      return spreadsheetConfig;
    }

    return null;
  }, [columns, parser, rows]);

  return {
    spreadsheet,
  };
};

export default useFundSummarySheet;
